import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import Nestable from 'react-nestable';

import useAttachedElementsStore from '../../stores/useAttachedElementsStore';
import Header from '../../components/properties/PropertiesHeader/PropertiesHeader';
import { LayerElement } from './LayerElement';
import useLayersStore from '../../stores/useLayersStore';
import useStageStore from '../../stores/useStageStore';
import usePropertiesStore from '../../stores/usePropertiesStore';
import { attachTransformerToSingleNode } from '../../utils/layerSelectionSync';
import { getLayerRelativeIdsForSelectedNodes } from '../../utils/layerSelectionSync';
import {
  ATTACH_LINK_ICON_COLOR_ATTR,
  LAYER_LABEL_COLOR_ATTR,
  applyAttachLinkIconColorToCluster,
  isAttachLinkedElement,
  setLayerLabelColorAttr,
} from '../../utils/attachLayerPartners';

import styles from './GraphicLayersPanel.module.css';
import 'react-nestable/dist/styles/index.css';

const Layers = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    refresh() {
      initFields();
    },
  }));

  const [rootGroupFields, setRootGroupFields] = useState([]);
  const selectedKeys = useLayersStore((s) => s.selectedKeys);
  const selectedRelatives = useLayersStore((s) => s.selectedRelatives);
  const collapsedKeys = useLayersStore((s) => s.collapsedKeys);
  const storeSetSelectedKeys = useLayersStore((s) => s.setSelectedKeys);
  const storeSetSelectedRelatives = useLayersStore((s) => s.setSelectedRelatives);
  const storeSetCollapsed = useLayersStore((s) => s.setCollapsed);
  const stage = useStageStore((s) => s.stage);
  const propertiesRefreshTrigger = usePropertiesStore((s) => s.propertiesRefreshTrigger);
  const maxDepth = props.maxDepth ?? 3;

  useEffect(() => {
    initFields();
  }, [stage, propertiesRefreshTrigger]);

  useEffect(() => {
    setSelectedItem();
  }, [propertiesRefreshTrigger, rootGroupFields]);

  const createElement = (
    field,
    parentKey,
    addChildren = false,
    parentCollapsed = false,
    collapsed = false,
    prevItemsMap = new Map(),
  ) => {
    return {
      title: getElementName(field),
      defaultName: getElementDefaultName(field),
      key: field._id,
      children: addChildren ? addChildrenElements(field, prevItemsMap) : [],
      draggable: true,
      field,
      elementType: field.attrs.elementType,
      parentKey,
      collapsed,
      parentCollapsed,
      layerLabelColor: field.getAttr(LAYER_LABEL_COLOR_ATTR) || '',
      attachLinkIconColor: field.getAttr(ATTACH_LINK_ICON_COLOR_ATTR) || '',
      isAttachLinked: isAttachLinkedElement(field),
    };
  };

  const buildPrevItemsMap = () => {
    const map = new Map();
    const traverse = (items) => {
      for (const item of items) {
        map.set(item.key, item);
        if (item.children.length) traverse(item.children);
      }
    };
    traverse(rootGroupFields);
    return map;
  };

  const addChildrenElements = (field, prevItemsMap) => {
    if (!field || !field.children) {
      return [];
    }
    const children = [...field.children];
    children.sort((a, b) => b.zIndex() - a.zIndex());

    return children.map((childrenItem) => {
      const prev = prevItemsMap.get(childrenItem._id);
      const parentIsCollapsed = prev?.parentCollapsed ?? collapsedKeys.has(field._id);
      return createElement(
        childrenItem,
        field._id,
        childrenItem.attrs.elementType === 'group',
        parentIsCollapsed,
        prev?.collapsed ?? (collapsedKeys.has(childrenItem._id) || parentIsCollapsed),
        prevItemsMap,
      );
    });
  };

  const getElementName = (field) => {
    if (field.getAttr('elementName')) {
      return field.getAttr('elementName');
    }
    return getElementDefaultName(field);
  };

  const getElementDefaultName = (field) => {
    const { elementType } = field.attrs;
    const elementsName = {
      rect: 'Rectangle',
      default: elementType.charAt(0).toUpperCase() + elementType.slice(1),
    };

    return `${elementsName[elementType] ?? elementsName.default} (${field._id})`;
  };

  const initFields = () => {
    const result = [];
    if (stage) {
      const prevItemsMap = buildPrevItemsMap();
      const layer = stage.findOne('#canvas');
      if (!layer) {
        setRootGroupFields([]);
        return;
      }
      const groupElements = layer.children.filter((el) =>
        el.hasName('elementGroup'),
      );
      groupElements.sort((a, b) => b.zIndex() - a.zIndex());

      if (groupElements && groupElements.length) {
        for (const el of groupElements) {
          const prev = prevItemsMap.get(el._id);
          const isCollapsedInStore = collapsedKeys.has(el._id);
          const item = createElement(
            el,
            undefined,
            el.attrs.elementType === 'group',
            prev?.parentCollapsed ?? isCollapsedInStore,
            false,
            prevItemsMap,
          );
          result.push(item);
        }
      }
    }
    setRootGroupFields(result);
  };

  const collapseToggle = (key, isCollapse) => {
    storeSetCollapsed(key, isCollapse);

    const collapseAll = (children, collapsed) =>
      children.map((child) => ({
        ...child,
        collapsed,
        children: child.children.length
          ? collapseAll(child.children, collapsed)
          : child.children,
      }));

    const toggleChildren = (items) =>
      items.map((item) => {
        if (item.key === key) {
          return {
            ...item,
            parentCollapsed: isCollapse,
            children: collapseAll(item.children, isCollapse),
          };
        }
        if (item.children.length) {
          return { ...item, children: toggleChildren(item.children) };
        }
        return item;
      });

    setRootGroupFields(toggleChildren(rootGroupFields));
  };

  const setSelectedItem = () => {
    let keys = [];
    if (!stage) return;
    const tr = stage.findOne('Transformer');
    if (!tr) return;
    const selectedNodes = tr.nodes();
    if (selectedNodes) {
      keys = selectedNodes.map((o) => o._id);
    }
    storeSetSelectedKeys(keys);
    storeSetSelectedRelatives(getLayerRelativeIdsForSelectedNodes(selectedNodes));
  };

  const isNodeChild = (key) => {
    return !rootGroupFields.map((o) => o.key).includes(key);
  };

  const getParentNode = (key) => {
    return getAllItems().find((o) =>
      o.children.map((c) => c.key).includes(key),
    );
  };

  const onDrop = (info) => {
    const { relativeNode } = info;
    const { dragNode } = info;
    const { field } = relativeNode;
    const dragField = dragNode.field;

    const layer = dragField.getLayer() || field.getLayer();
    const stage = layer.getParent();

    if (isNodeChild(dragNode.key)) {
      if (isNodeChild(relativeNode.key)) {
        const parent = getParentNode(relativeNode.key).field;
        const absPos = dragField.absolutePosition();

        dragField.draggable(false);
        dragField.moveTo(parent);
        dragField.absolutePosition(absPos);
      } else {
        const parent = getParentNode(dragNode.key).field;
        const absPos = dragField.absolutePosition();

        dragField.moveTo(layer);
        dragField.absolutePosition(absPos);

        if (parent.hasName('distributed')) {
          dragField.position(layer.findOne('#canvas_bg').position());
        }
        dragField.draggable(true);
        if (!parent.children.length) {
          props.destroyGroup(parent);
        }
      }
    } else if (isNodeChild(relativeNode.key)) {
      const parent = getParentNode(relativeNode.key).field;
      const absPos = dragField.absolutePosition();

      dragField.draggable(false);
      dragField.moveTo(parent);
      dragField.absolutePosition(absPos);
    }
    dragField.zIndex(field.zIndex());
    initFields();

    const tr = stage.findOne('Transformer');
    if (tr) {
      const sel = tr.nodes();
      if (sel.length === 1) {
        attachTransformerToSingleNode(tr, sel[0]);
      } else if (sel.length > 1) {
        const copy = sel.slice();
        tr.nodes([]);
        tr.nodes(copy);
      }
    }

    if (layer) {
      layer.find('.distributed').forEach((el) => useAttachedElementsStore.getState().reDistributeGroup(el));
    }
    props.saveHistory(stage);
  };

  const getAllItems = () => {
    let found = true;

    let currentItems = [...rootGroupFields];
    let result = [...currentItems];
    while (found) {
      const children = currentItems.map((t) => t.children).flat();
      if (children.length) {
        result = [...result, ...children];
        currentItems = children;
      } else {
        found = false;
      }
    }

    return result;
  };

  const onSelect = (e, key) => {
    const isMetaPressed = e.shiftKey || e.ctrlKey || e.metaKey;

    const allItems = getAllItems();
    const item = allItems.find((t) => t.key === key);

    if (!item) {
      return;
    }

    let newKeys = [key];

    if (isMetaPressed) {
      if (selectedKeys.find((t) => t === key)) {
        newKeys = selectedKeys.filter((t) => t !== key);
      } else {
        const selected = allItems.filter((t) => selectedKeys.includes(t.key));
        if (selected.length) {
          const first = selected[0];
          if (first.parentKey === item.parentKey) {
            newKeys = [...selectedKeys, key];
          }
        }
      }
    }

    storeSetSelectedKeys(newKeys);

    const nodes = allItems
      .filter((t) => newKeys.includes(t.key))
      .map((t) => t.field);

    storeSetSelectedRelatives(getLayerRelativeIdsForSelectedNodes(nodes));

    props.onSelectField(nodes);
  };

  const confirmChange = ({ dragItem, destinationParent }) => {
    return !destinationParent || destinationParent.elementType === 'group';
  };

  const compareGroups = (previousGroup, newGroup) => {
    if (previousGroup === newGroup) return true;
    if (typeof previousGroup !== 'object' || typeof newGroup !== 'object')
      return false;

    const keys1 = Object.keys(previousGroup);
    const keys2 = Object.keys(newGroup);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (
        !keys2.includes(key) ||
        !compareGroups(previousGroup[key], newGroup[key])
      ) {
        return false;
      }
    }
    return true;
  };

  const onDragFinished = (data) => {
    const { items } = data;
    const { dragItem } = data;
    const { targetPath } = data;

    let item = null;
    let parent = null;
    for (let i = 0; i < targetPath.length; i++) {
      const levelIndex = targetPath[i];
      parent = item;
      item =
        item && item.children ? item.children[levelIndex] : items[levelIndex];
    }

    const currLevelItems = parent ? parent.children : items;
    const indexOfDragItem = currLevelItems.indexOf(dragItem);

    const prevGroup = getParentNode(dragItem.key)?.children ?? rootGroupFields;
    const prevIndex = prevGroup.indexOf(
      prevGroup.find((t) => t.key === dragItem.key),
    );

    if (compareGroups(prevGroup, currLevelItems)) return;

    const prevNode = currLevelItems[indexOfDragItem - 1];
    const nextNode = currLevelItems[indexOfDragItem + 1];

    const relativeNode = indexOfDragItem > prevIndex ? prevNode : nextNode;

    if (dragItem) {
      onDrop({
        dragNode: dragItem,
        relativeNode: relativeNode ?? prevNode ?? nextNode,
      });
    }
  };

  const onNameChanged = (key, title) => {
    const allItems = getAllItems();
    const item = allItems.find((t) => t.key === key);
    if (item) {
      item.field.setAttr('elementName', title);
      item.title = title;
      if (props.onChangeName) {
        props.onChangeName();
      }
      if (stage && props.saveHistory) {
        props.saveHistory(stage);
      }
    }
  };

  const onLayerLabelColorChanged = (key, color) => {
    const allItems = getAllItems();
    const item = allItems.find((t) => t.key === key);
    if (!item || !stage) return;

    setLayerLabelColorAttr(item.field, color);
    initFields();
    if (props.onChangeName) {
      props.onChangeName();
    }
    props.saveHistory(stage);
  };

  const onAttachLinkIconColorChanged = (key, color) => {
    const allItems = getAllItems();
    const item = allItems.find((t) => t.key === key);
    if (!item || !stage) return;

    applyAttachLinkIconColorToCluster(stage, item.field, color);
    initFields();
    if (props.onChangeName) {
      props.onChangeName();
    }
    props.saveHistory(stage);
  };

  const deleteElement = (item) => {
    if (props.onDeleteElement) {
      props.onDeleteElement(item?.field);
    }
  };

  const copyElement = (item) => {
    if (props.onCopyElement) {
      props.onCopyElement(item?.field);
    }
  };

  const onElementHide = (item) => {
    props.hideElement(item.field);
    if (item.elementType === 'group' && item.children.length) {
      const hideRecursive = (items) => {
        items.forEach((child) => {
          props.hideElement(child.field);
          if (child.children?.length) hideRecursive(child.children);
        });
      };
      hideRecursive(item.children);
      collapseToggle(item.key, item.field.attrs.opacity === 0);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Header onClose={props.onClose} caption='Layers' />
      </div>
      <Nestable
        confirmChange={confirmChange}
        onChange={onDragFinished}
        idProp='key'
        items={rootGroupFields}
        maxDepth={maxDepth + 1}
        threshold={5}
        renderItem={({ item }) => (
          <LayerElement
            onCopyElement={copyElement}
            onDeleteElement={deleteElement}
            onNameChanged={onNameChanged}
            onLayerLabelColorChanged={onLayerLabelColorChanged}
            onAttachLinkIconColorChanged={onAttachLinkIconColorChanged}
            item={item}
            onSelectElement={onSelect}
            collapseToggle={collapseToggle}
            onElementHide={onElementHide}
            isSelected={!!(selectedKeys ?? []).find((t) => t === item.key)}
            selectedByRelative={
              !!(selectedRelatives ?? []).find((t) => t === item.key)
            }
          />
        )}
      />
    </div>
  );
});

export default Layers;
